<template>
  <div class="subtitle-settings" :class="{ compact }">
    <h4 class="section-title">{{ t('speechTranslation.subtitleSection') }}</h4>
    <p v-if="!compact" class="section-desc">{{ t('speechTranslation.subtitleDesc') }}</p>
    <fieldset class="subtitle-fieldset" :disabled="props.readOnly">
    <a-form layout="vertical" :model="form" class="settings-form" :size="compact ? 'small' : 'middle'">
      <a-row :gutter="compact ? 0 : 16">
        <a-col :span="compact ? 24 : 12">
          <a-form-item :label="t('speechTranslation.backgroundColor')">
            <a-space :size="compact ? 8 : 8" align="center">
              <input v-model="form.backgroundColor" type="color" class="color-input" :class="{ compact }" />
              <a-input
                v-model:value="form.backgroundColor"
                placeholder="#CCFFCC"
                :style="compact ? { width: '88px', minWidth: '88px' } : { width: '90px' }"
              />
            </a-space>
          </a-form-item>
        </a-col>
        <a-col v-if="!compact" :span="12">
          <a-form-item :label="t('speechTranslation.opacity')">
            <a-select v-model:value="form.opacity" style="width: 100%">
              <a-select-option v-for="n in 21" :key="(n - 1) * 5" :value="(n - 1) * 5">{{ (n - 1) * 5 }}%</a-select-option>
            </a-select>
          </a-form-item>
        </a-col>
      </a-row>
      <a-row v-if="compact" :gutter="0">
        <a-col :span="24">
          <a-form-item :label="t('speechTranslation.opacity')">
            <a-select v-model:value="form.opacity" style="width: 100%" :size="'small'">
              <a-select-option v-for="n in 21" :key="(n - 1) * 5" :value="(n - 1) * 5">{{ (n - 1) * 5 }}%</a-select-option>
            </a-select>
          </a-form-item>
        </a-col>
      </a-row>
      <a-row :gutter="compact ? 0 : 16">
        <a-col :span="compact ? 24 : 12">
          <a-form-item :label="t('speechTranslation.fontColor')">
            <a-space :size="compact ? 8 : 8" align="center">
              <input v-model="form.fontColor" type="color" class="color-input" :class="{ compact }" />
              <a-input
                v-model:value="form.fontColor"
                placeholder="#000000"
                :style="compact ? { width: '88px', minWidth: '88px' } : { width: '90px' }"
              />
            </a-space>
          </a-form-item>
        </a-col>
        <a-col :span="compact ? 24 : 12">
          <a-form-item :label="t('speechTranslation.fontSize')">
            <a-select v-model:value="form.fontSize" style="width: 100%">
              <a-select-option :value="12">12px</a-select-option>
              <a-select-option :value="14">14px</a-select-option>
              <a-select-option :value="16">16px</a-select-option>
              <a-select-option :value="18">18px</a-select-option>
              <a-select-option :value="20">20px</a-select-option>
              <a-select-option :value="24">24px</a-select-option>
              <a-select-option :value="28">28px</a-select-option>
              <a-select-option :value="32">32px</a-select-option>
            </a-select>
          </a-form-item>
        </a-col>
      </a-row>
      <a-form-item :label="t('speechTranslation.fontFamily')">
        <a-select v-model:value="form.fontFamily" style="width: 100%" :size="compact ? 'small' : 'middle'">
          <a-select-option value="Microsoft YaHei">{{ t('speechTranslation.fontMicrosoftYaHei') }}</a-select-option>
          <a-select-option value="PingFang SC">{{ t('speechTranslation.fontPingFang') }}</a-select-option>
          <a-select-option value="SimSun">{{ t('speechTranslation.fontSimSun') }}</a-select-option>
          <a-select-option value="SimHei">{{ t('speechTranslation.fontSimHei') }}</a-select-option>
          <a-select-option value="Arial">{{ t('speechTranslation.fontArial') }}</a-select-option>
          <a-select-option value="sans-serif">{{ t('speechTranslation.fontSystemDefault') }}</a-select-option>
        </a-select>
      </a-form-item>
      <a-form-item :label="t('speechTranslation.lineSpacing')">
        <a-select v-model:value="form.lineSpacing" style="width: 100%" :size="compact ? 'small' : 'middle'">
          <a-select-option :value="1">1</a-select-option>
          <a-select-option :value="1.2">1.2</a-select-option>
          <a-select-option :value="1.5">1.5</a-select-option>
          <a-select-option :value="1.8">1.8</a-select-option>
          <a-select-option :value="2">2</a-select-option>
          <a-select-option :value="2.5">2.5</a-select-option>
        </a-select>
      </a-form-item>
      <a-form-item :label="t('speechTranslation.pcBackgroundImage')" :extra="!compact ? t('speechTranslation.backgroundImageExtra') : undefined">
        <div class="bg-image-row">
          <input
            ref="pcFileInputRef"
            type="file"
            accept="image/*"
            class="file-input"
            @change="onPcFileChange"
          />
          <a-button size="small" @click="() => (pcFileInputRef as HTMLInputElement)?.click()" :loading="uploadingPC">
            {{ form.backgroundImagePC ? t('speechTranslation.replace') : t('speechTranslation.upload') }}
          </a-button>
          <template v-if="form.backgroundImagePC">
            <img :src="form.backgroundImagePC" class="bg-preview" :alt="t('speechTranslation.pcBgAlt')" />
            <a-button size="small" danger @click="clearBg('PC')">{{ t('speechTranslation.clear') }}</a-button>
          </template>
        </div>
      </a-form-item>
      <a-form-item :label="t('speechTranslation.h5BackgroundImage')" :extra="!compact ? t('speechTranslation.backgroundImageExtra') : undefined">
        <div class="bg-image-row">
          <input
            ref="h5FileInputRef"
            type="file"
            accept="image/*"
            class="file-input"
            @change="onH5FileChange"
          />
          <a-button size="small" @click="() => (h5FileInputRef as HTMLInputElement)?.click()" :loading="uploadingH5">
            {{ form.backgroundImageH5 ? t('speechTranslation.replace') : t('speechTranslation.upload') }}
          </a-button>
          <template v-if="form.backgroundImageH5">
            <img :src="form.backgroundImageH5" class="bg-preview" :alt="t('speechTranslation.h5BgAlt')" />
            <a-button size="small" danger @click="clearBg('H5')">{{ t('speechTranslation.clear') }}</a-button>
          </template>
        </div>
      </a-form-item>
    </a-form>
    </fieldset>
  </div>
</template>

<script setup lang="ts">
import { reactive, watch, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { message } from 'ant-design-vue'
import { uploadBackgroundImage, getBackgroundImageUrl } from '../api/session'

const { t } = useI18n()

export interface SubtitleStyle {
  backgroundColor: string
  opacity: number
  fontColor: string
  fontSize: number
  fontFamily: string
  lineSpacing: number
  backgroundImagePC?: string
  backgroundImageH5?: string
}

const defaultStyle: SubtitleStyle = {
  // 默认浅绿色背景 + 黑色文字
  backgroundColor: '#CCFFCC',
  opacity: 80,
  fontColor: '#000000',
  fontSize: 18,
  fontFamily: 'Microsoft YaHei',
  lineSpacing: 1.5,
  backgroundImagePC: '',
  backgroundImageH5: ''
}

const pcFileInputRef = ref<HTMLInputElement | null>(null)
const h5FileInputRef = ref<HTMLInputElement | null>(null)
const uploadingPC = ref(false)
const uploadingH5 = ref(false)

const props = withDefaults(
  defineProps<{ compact?: boolean; readOnly?: boolean }>(),
  { compact: false, readOnly: false }
)

const form = reactive<SubtitleStyle>({ ...defaultStyle })

const emit = defineEmits<{
  (e: 'change', v: SubtitleStyle): void
}>()

function emitChange() {
  emit('change', { ...form })
}

watch(form, () => emitChange(), { deep: true })

async function doUpload(file: File, which: 'PC' | 'H5') {
  const loading = which === 'PC' ? uploadingPC : uploadingH5
  loading.value = true
  try {
    const res = await uploadBackgroundImage(file)
    const url = getBackgroundImageUrl(res)
    if (url) {
      if (which === 'PC') form.backgroundImagePC = url
      else form.backgroundImageH5 = url
    } else {
      message.error(t('speechTranslation.uploadNoUrl'))
    }
  } catch (e: any) {
    console.error('upload background image failed:', e)
  } finally {
    loading.value = false
  }
}

function onPcFileChange(e: Event) {
  const input = e.target as HTMLInputElement
  const file = input.files?.[0]
  if (file) {
    doUpload(file, 'PC')
    input.value = ''
  }
}

function onH5FileChange(e: Event) {
  const input = e.target as HTMLInputElement
  const file = input.files?.[0]
  if (file) {
    doUpload(file, 'H5')
    input.value = ''
  }
}

function clearBg(which: 'PC' | 'H5') {
  if (which === 'PC') {
    form.backgroundImagePC = ''
    pcFileInputRef.value && (pcFileInputRef.value.value = '')
  } else {
    form.backgroundImageH5 = ''
    h5FileInputRef.value && (h5FileInputRef.value.value = '')
  }
}

/** 页面写死的默认字幕样式，与后端 Redis 默认值一致 */
function getDefaultStyle(): SubtitleStyle {
  return { ...defaultStyle }
}

function setStyle(style: Partial<SubtitleStyle>) {
  Object.assign(form, { ...defaultStyle, ...style })
}

defineExpose({
  getStyle: () => ({ ...form }),
  getDefaultStyle,
  reset: () => Object.assign(form, { ...defaultStyle }),
  setStyle,
})
</script>

<style scoped lang="less">
.subtitle-settings {
  padding: 16px;
  background: #fafafa;
  border-radius: 8px;

  &.compact {
    padding: 12px 16px;
    min-height: 260px;

    .section-title {
      font-size: 13px;
      margin-bottom: 10px;
    }

    .section-desc {
      display: none;
    }

    :deep(.ant-form-item) {
      margin-bottom: 10px;
    }

    .color-input.compact {
      width: 28px;
      height: 22px;
    }
  }
}

.section-title {
  margin: 0 0 4px 0;
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
}

.section-desc {
  margin: 0 0 16px 0;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
}

.settings-form {
  min-width: 0;
  overflow: hidden;

  :deep(.ant-form-item) {
    margin-bottom: 12px;
  }

  :deep(.ant-input) {
    min-width: 0;
  }
}

.color-input {
  width: 36px;
  height: 28px;
  padding: 2px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  cursor: pointer;
}

.opacity-value {
  margin-left: 8px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.65);
}

.file-input {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
}

.bg-image-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.bg-preview {
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #d9d9d9;
}

.subtitle-fieldset {
  border: none;
  margin: 0;
  padding: 0;
  min-width: 0;
}
</style>
